import type { DigitalData, SearchDetailsDataProps } from '../types/types';
import set from 'lodash/set';
import unset from 'lodash/unset';

export const createPropertySearchDetailsString = (props: SearchDetailsDataProps): string => {
  const searchDetailsString = `RM:${props.numRooms}|AD:${props.numAdults}|CH:${
    props.numChildren
  }|CC:${props?.currencyCode || 'LOCAL'}${props?.datesFlex ? '|FLX' : ''}${
    props?.isUsePoints ? '|PT' : ''
  }${props?.promoCode ? '|PRO' : ''}${props?.groupCode ? '|GRP' : ''}${
    props?.corporateCode ? '|CO' : ''
  }${props?.isTravelAgents ? '|AG' : ''}${props?.isTripleA ? '|AAA' : ''}${
    props?.isAarp ? '|ARP' : ''
  }${props?.isSenior ? '|SEN' : ''}${props?.isGovernment ? '|GV' : ''}${
    !!props?.isOwner || !!props?.isHGVOwner ? '|ON' : ''
  }${props?.isEmployee ? '|TM' : ''}${props?.isFriendsAndFamily ? '|FF' : ''}${
    props?.isSMB ? '|SMB' : ''
  }${props?.isCBP ? '|CBP' : ''}${props?.isLTA ? '|H4L' : ''}`;
  return searchDetailsString;
};

const HONORS_TIER_CODES = {
  member: 'B',
  blue: 'B',
  silver: 'S',
  gold: 'G',
  diamond: 'D',
  'lifetime diamond': 'L',
} as const;

type ValidTierCodes = ValuesOf<typeof HONORS_TIER_CODES>;

const VALID_TIER_CODES = Object.values(HONORS_TIER_CODES) as readonly ValidTierCodes[];

const isValidTierCode = (code: string): code is ValidTierCodes => {
  return (VALID_TIER_CODES as readonly string[]).includes(code);
};

/**
 * Normalizes a string value to a valid Hilton Honors tier code
 * Accepts various forms of tier names or codes and converts them to standard tier codes:
 * - B: Member (formerly Blue)
 * - S: Silver
 * - G: Gold
 * - D: Diamond
 * - L: Lifetime Diamond
 *
 * @param value - The tier name or code to normalize
 * @returns The standard tier code or empty if input is invalid
 */
export const normalizeHonorsTier = (value: string | undefined) => {
  if (!value) return '';

  const code = value.trim().toUpperCase();
  if (isValidTierCode(code)) {
    return code;
  }

  const tierName = value.trim().toLowerCase();
  return HONORS_TIER_CODES[tierName as keyof typeof HONORS_TIER_CODES] || '';
};

export const setDigitalData = (path: string, value: unknown) => {
  if (path.includes('rewardsTier') && typeof value === 'string') {
    value = normalizeHonorsTier(value);
  }
  window?.digitalData && set(window.digitalData, path, value);
};

/** Dedupe segment IDs → comma-delimited string, or undefined when empty. */
export function formatGlobalUserSegment(segmentIds: string[]): string | undefined {
  const deduped = [...new Set(segmentIds)];
  if (deduped.length === 0) {
    return undefined;
  }
  return deduped.join(',');
}

/** Set or unset global.user.segment on the provided digitalData store. */
export function setGlobalUserSegment(digitalData: DigitalData, segmentIds: string[]): void {
  const value = formatGlobalUserSegment(segmentIds);
  if (value !== undefined) {
    set(digitalData, 'global.user.segment', value);
  } else {
    unset(digitalData, 'global.user.segment');
  }
}

type UserPackage = Record<'packageName', string>;

export const preprocessUserPackagesForMetrics = (packages?: UserPackage[]) =>
  packages?.filter(Boolean)?.map((pack) => pack && pack.packageName) ?? null;

/**
 * @param packages An array of package names or an array of objects containing package names
 * @returns
 */
export const formatUserPackages = (packages: UserPackage[] | string[]): string => {
  return packages.map((pkg) => (typeof pkg === 'string' ? pkg : pkg.packageName)).join(', ');
};
